import { CalendarEvent } from '../../types/crm';

/** Course delivery language for calendar session events (from resource or legacy courseName meta). */
export function getCalendarSessionLanguage(event: CalendarEvent): string {
  const resource = event.resource as { sessionLanguage?: string } | undefined;
  const fromResource = String(resource?.sessionLanguage ?? '').trim();
  if (fromResource && fromResource.toLowerCase() !== 'no language') return fromResource;

  const fromCourseMeta = String(event.courseName || '').match(/^(.+?)\s*\|/);
  if (fromCourseMeta) {
    const lang = fromCourseMeta[1].trim();
    if (lang && lang.toLowerCase() !== 'no language') return lang;
  }

  const fromTitle = String(event.title || '').match(/\(([^)]+)\)\s*$/);
  if (fromTitle) {
    const lang = fromTitle[1].trim();
    if (lang) return lang;
  }

  return '';
}

/** Display title for session cards, e.g. "Safe Pass Polish". */
export function formatCalendarSessionTitle(event: CalendarEvent): string {
  const base = String(event.companyName || event.title || 'Training')
    .replace(/\s*\([^)]+\)\s*$/, '')
    .trim();
  const lang = getCalendarSessionLanguage(event);
  return lang ? `${base} ${lang}`.trim() : base;
}

export type BlockedDayStatus = {
  severity: 'warn' | 'block';
  reason?: string;
};

/** Label shown on dashboard calendar for a blocked/warning day. */
export function formatBlockedDayCalendarLabel(status: BlockedDayStatus): string {
  const custom = String(status.reason ?? '').trim();
  if (custom) return custom;
  return status.severity === 'block' ? 'Blocked day' : 'Warning day';
}

/** Month/week grid cell styling — blocked days take priority over “today” highlight. */
export function blockedDayCellClasses(
  blockedStatus: BlockedDayStatus | null | undefined,
  opts?: { isToday?: boolean }
): string {
  if (blockedStatus?.severity === 'block') {
    return 'border-red-500 dark:border-red-600 bg-red-100 dark:bg-red-950/55 ring-2 ring-red-300 dark:ring-red-800 shadow-md';
  }
  if (blockedStatus?.severity === 'warn') {
    return 'border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/45 ring-1 ring-amber-200 dark:ring-amber-800';
  }
  if (opts?.isToday) {
    return 'border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 ring-2 ring-blue-200 dark:ring-blue-700 shadow-md';
  }
  return 'border-slate-200 dark:border-slate-700';
}

export function isCalendarEventCancelled(event: CalendarEvent): boolean {
  return (event.status || '').toLowerCase() === 'cancelled';
}

export function isoDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Shared day label formatter used by schedule/table views. */
export function formatDayDateLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatShortUkDate(date: Date): string {
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Noon local time — avoids DST / timezone off-by-one in calendar grids. */
export function noonDate(year: number, month: number, day: number): Date {
  return new Date(year, month, day, 12, 0, 0);
}

export function parseIsoDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return noonDate(y!, m! - 1, d!);
}

export function startOfMonth(date: Date): Date {
  return noonDate(date.getFullYear(), date.getMonth(), 1);
}

export function endOfMonth(date: Date): Date {
  return noonDate(date.getFullYear(), date.getMonth() + 1, 0);
}

/** Monday-start week containing `date`. */
export function mondayOfWeekContaining(date: Date): Date {
  const dayOfWeek = date.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  return noonDate(date.getFullYear(), date.getMonth(), date.getDate() + mondayOffset);
}

export function hasActiveScheduleDateRange(range: { from: string; to: string }): boolean {
  return Boolean(range.from && range.to);
}

export function eachDayInIsoRange(from: string, to: string): Date[] {
  const days: Date[] = [];
  let cur = parseIsoDay(from);
  const end = parseIsoDay(to);
  while (cur.getTime() <= end.getTime()) {
    days.push(new Date(cur));
    cur = noonDate(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
  }
  return days;
}

export function calendarEventIsoDate(event: CalendarEvent): string {
  if (event.date instanceof Date) {
    return isoDayKey(event.date);
  }
  const dateString = String(event.date || '');
  if (dateString.includes('T')) return dateString.split('T')[0];
  return dateString;
}

export function sortCalendarEventsByDateTime(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((a, b) => {
    const dateCmp = calendarEventIsoDate(a).localeCompare(calendarEventIsoDate(b));
    if (dateCmp !== 0) return dateCmp;
    return String(a.startTime || '').localeCompare(String(b.startTime || ''));
  });
}

/** Lines per event card: allow richer data in month/week cards. */
export function linesBudgetForCard(calendarView: 'month' | 'week', totalEventsInDay: number): number {
  if (calendarView === 'week') return 8;
  return totalEventsInDay <= 1 ? 8 : 6;
}

/** Max course “cards” before expand: month 2, week 4 */
export function maxVisibleEvents(calendarView: 'month' | 'week'): number {
  return calendarView === 'month' ? 2 : 4;
}

function deterministicNumericRef(sourceId: string): number {
  const clean = String(sourceId || '').replace(/-/g, '').toUpperCase();
  let hash = 0;
  for (let i = 0; i < clean.length; i += 1) {
    hash = (hash * 31 + clean.charCodeAt(i)) % 99999;
  }
  return Math.max(1, hash);
}

export function trainingReferenceCode(sequence: number): string {
  return `TT${String(Math.max(1, sequence)).padStart(5, '0')}`;
}

export function bookingReferenceCode(bookingId: string, sequence?: number): string {
  if (sequence != null) return trainingReferenceCode(sequence);
  return trainingReferenceCode(deterministicNumericRef(bookingId));
}

export function sessionReferenceCode(sessionId: string, sequence?: number): string {
  if (sequence != null) return trainingReferenceCode(sequence);
  return trainingReferenceCode(deterministicNumericRef(sessionId));
}

/** Parse TT12345 / tt12345 / 12345 → numeric ref (12345). */
export function parseTrainingReferenceDigits(term: string): number | null {
  const t = String(term || '').trim().toUpperCase().replace(/\s+/g, '');
  const match = t.match(/^TT0*(\d{1,5})$/) || t.match(/^0*(\d{1,5})$/);
  if (!match) return null;
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** True when query looks like a TT booking/session reference. */
export function looksLikeTrainingReferenceQuery(term: string): boolean {
  const t = String(term || '').trim().toUpperCase().replace(/\s+/g, '');
  return /^TT?\d{1,5}$/.test(t);
}

/** Match search text against a TT##### reference (exact or partial while typing). */
export function trainingReferenceMatchesQuery(query: string, refCode: string): boolean {
  const qNorm = String(query || '').trim().toUpperCase().replace(/\s+/g, '');
  const refNorm = String(refCode || '').trim().toUpperCase().replace(/\s+/g, '');
  if (!qNorm || !refNorm) return false;
  if (qNorm === refNorm) return true;

  const qDigits = parseTrainingReferenceDigits(qNorm);
  const refDigits = parseTrainingReferenceDigits(refNorm);
  if (qDigits != null && refDigits != null) {
    if (qDigits === refDigits) return true;
    if (looksLikeTrainingReferenceQuery(qNorm)) {
      const qTail = qNorm.replace(/^TT/i, '');
      // Full TT##### typed → exact numeric match only (avoids hash-ref false positives).
      if (/^\d{5}$/.test(qTail)) return false;
      const refPadded = String(refDigits).padStart(5, '0');
      return refPadded.startsWith(qTail);
    }
    return false;
  }

  return refNorm.includes(qNorm);
}

export function buildSessionReferenceIndex(
  sessions: Array<{ id: string; dateBlocks?: Array<{ date?: string }>; courseName?: string; createdAt?: string }>
): Map<string, number> {
  const sorted = [...sessions].sort((a, b) => {
    const aDate = a.dateBlocks?.[0]?.date || '';
    const bDate = b.dateBlocks?.[0]?.date || '';
    if (aDate !== bDate) return aDate.localeCompare(bDate);
    const nameCmp = String(a.courseName || '').localeCompare(String(b.courseName || ''));
    if (nameCmp !== 0) return nameCmp;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
  const map = new Map<string, number>();
  sorted.forEach((s, i) => map.set(s.id, i + 1));
  return map;
}

export function buildBookingReferenceIndex(
  bookings: Array<{ id: string; courseDate?: string; createdAt?: string }>
): Map<string, number> {
  const sorted = [...bookings].sort((a, b) => {
    const aDate = String(a.courseDate || '');
    const bDate = String(b.courseDate || '');
    if (aDate !== bDate) return aDate.localeCompare(bDate);
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
  });
  const map = new Map<string, number>();
  sorted.forEach((b, i) => map.set(b.id, i + 1));
  return map;
}

export function shortVenueLabel(city?: string | null, fallbackLine1?: string | null): string | undefined {
  const c = (city || '').trim();
  if (c) return c.length <= 14 ? c : c.split(/\s+/)[0];
  const f = (fallbackLine1 || '').trim();
  if (!f) return undefined;
  const firstWord = f.split(/\s+/)[0];
  return firstWord && firstWord.length <= 14 ? firstWord : undefined;
}

export function spotsAvailabilityClass(event: CalendarEvent): string {
  const cap = event.resource?.capacity;
  const booked = event.resource?.bookedCount ?? event.attendeeCount ?? 0;
  if (cap == null || cap <= 0) return 'text-slate-500 dark:text-slate-400';
  return cap - booked > 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-500';
}

/** Embedded machine-readable blocks (same markers as CourseSessionsPanel). */
const POST_SESSION_META_PREFIX = '[POST_SESSION_META]';
const CARD_META_PREFIX = '[CARD_META]';

/**
 * Human-visible notes for calendar UI — strips post-session JSON and card-distribution meta
 * that are appended to `session_other_info` / notes fields.
 */
export function stripCalendarNoteMeta(text: string | null | undefined): string {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const withoutPost = raw.split(POST_SESSION_META_PREFIX)[0].trim();
  return withoutPost.split(CARD_META_PREFIX)[0].trim();
}

/** Venue / delivery mode from free-text notes — same keyword rules as bookings CalendarView. */
export function getVenueTypeLabelFromNotes(...texts: (string | undefined | null)[]): string {
  const combined = texts.filter(Boolean).join(' ').toLowerCase();
  if (!combined.trim()) return '—';
  if (combined.includes('in class')) return 'IN CLASS';
  if (combined.includes('online')) return 'ONLINE';
  if (combined.includes('blended')) return 'BLENDED';
  if (combined.includes('on site')) return 'ON SITE';
  return '—';
}

/** Maps course_sessions.delivery_mode (or legacy text) to table labels. */
export function formatDeliveryModeLabel(raw: string | undefined | null): string | null {
  if (raw == null || String(raw).trim() === '') return null;
  const v = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
  if (v === 'in_class' || v.includes('in_class') || v.includes('in-class')) return 'IN CLASS';
  if (v === 'online' || v.includes('online')) return 'ONLINE';
  if (v.includes('blended')) return 'BLENDED';
  if (v === 'on_site' || v.includes('on_site') || v.includes('on-site') || v.includes('on site')) return 'ON SITE';
  return String(raw).trim().toUpperCase();
}

export function getVenueTypeLabelForCalendarEvent(event: CalendarEvent): string {
  const fromDb = formatDeliveryModeLabel(event.resource?.deliveryMode);
  if (fromDb) return fromDb;
  return getVenueTypeLabelFromNotes(
    stripCalendarNoteMeta(event.resource?.sessionNotes),
    stripCalendarNoteMeta(event.resource?.notes),
    event.courseName,
    event.companyName
  );
}
